import { makeStyles } from '@material-ui/styles';
import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import styles from '../styles';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteIcon from '@material-ui/icons/Delete';
import { ExpansionPanel, ExpansionPanelSummary, ExpansionPanelDetails, Typography } from '@material-ui/core';
import cn from 'classnames';
import * as Yup from 'yup';

const useStyles = makeStyles(styles);

const FieldArray = ({
  children,
  label,
  maxItems = null,
  useAccordion = false,

  // KEEP THESE AS COMPONENTS FOR RENDERING PURPOSES
  AccordionTitle,
  AccordionContent,
  Content,

  ...fieldArrayProps
}) => {
  /**
   * FIELD ARRAY VARIABLES:
   * append, fields, insert, move, prepend, remove, replace, swap, update
   */
  const fieldArrayMethods = useFieldArray(fieldArrayProps);
  const context = useFormContext();
  const classes = useStyles();

  const canAddAnotherItem = !maxItems || fieldArrayMethods.fields.length < maxItems;

  const addItem = () => {
    if (!canAddAnotherItem) {
      return;
    }

    const defaultObject = context.schema
      ? Yup.reach(context.schema, fieldArrayProps.name)?.innerType?.getDefault()
      : {};

    fieldArrayMethods.append(defaultObject);
  };

  const removeItem = index => {
    fieldArrayMethods.remove(index);
  };

  // console.log('RERENDER: FieldArray');

  return (
    <div className={classes.inputContainer}>
      <p
        style={{
          marginBottom: 5,
          fontWeight: 400,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
        className={cn(
          classes.inputLabel,
          // hasError && classes.textLabelError
        )}
        title={canAddAnotherItem ? 'Add a new entry' : 'Max entries reached'}
      >
        <AddCircleIcon
          fontSize={'small'}
          className={cn('list-action-icon', 'mr-10')}
          style={{
            color: canAddAnotherItem ? 'green' : 'gray',
            cursor: 'pointer',
          }}
          onClick={addItem}
        />
        {label && `${label}: `}
        {/* {!!error && error} */}
      </p>

      {fieldArrayMethods.fields.map((data, fieldIndex, original) => {
        // console.log('RERENDER: FieldArray index', fieldIndex);

        // allow null so for non-object nested schemas, it will just compute the indexed path
        const buildPath = (name = null) => `${fieldArrayProps.name}.[${fieldIndex}]${name ? `.${name}` : ''}`;

        return useAccordion ? (
          <div style={{ width: '100%' }} key={data.id}>
            <ExpansionPanel>
              <ExpansionPanelSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                <div className={cn('row-start-center flex-1 text-left')}>
                  <DeleteIcon
                    fontSize={'small'}
                    className={cn('list-action-icon')}
                    style={{
                      color: 'red',
                      marginLeft: -10,
                      marginRight: 10,
                    }}
                    onClick={evt => {
                      evt.stopPropagation();
                      removeItem(fieldIndex);
                    }}
                  />
                  <Typography
                    className={cn(classes.heading, 'flex-1')}
                    style={Object.assign(
                      {},
                      // errorIndexes.includes(childIndex) && {
                      //   color: 'red',
                      // },
                    )}
                  >
                    {AccordionTitle && (
                      <AccordionTitle
                        data={data}
                        fieldIndex={fieldIndex}
                        fieldArrayMethods={fieldArrayMethods}
                        buildPath={buildPath}
                      />
                    )}
                    {!AccordionTitle && `Item: ${fieldIndex + 1}`}
                  </Typography>
                </div>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                {!!AccordionContent && (
                  <AccordionContent
                    data={data}
                    fieldIndex={fieldIndex}
                    fieldArrayMethods={fieldArrayMethods}
                    buildPath={buildPath}
                  />
                )}
              </ExpansionPanelDetails>
            </ExpansionPanel>
          </div>
        ) : (
          <div style={{ width: '100%' }} key={data.id}>
            <div className={cn('row-start-center flex-1 text-left')}>
              <DeleteIcon
                fontSize={'small'}
                className={cn('list-action-icon')}
                style={{
                  color: 'red',
                  marginLeft: -10,
                  marginRight: 10,
                }}
                onClick={evt => {
                  evt.stopPropagation();
                  removeItem(fieldIndex);
                }}
              />

              <Content
                data={data}
                fieldIndex={fieldIndex}
                fieldArrayMethods={fieldArrayMethods}
                buildPath={buildPath}
              />
            </div>
          </div>
        );
      })}

      {!fieldArrayMethods.fields.length && (
        <div className={cn(classes.disabledText)} style={{ marginLeft: 15 }}>
          No list items... Please add one
        </div>
      )}
    </div>
  );
};

export default FieldArray;
